import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IMAGES, VIDEO } from '../data/site';
export function HeroBackground({
  sectionRef
}: {
  sectionRef: React.RefObject<HTMLElement>;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wantsPlayback = useRef(false);
  const hasManualChoice = useRef(false);
  const isInView = useRef(false);
  const hasFailed = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasVideoFrame, setHasVideoFrame] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const syncPlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (wantsPlayback.current && isInView.current && !document.hidden && !hasFailed.current) {
      // No source is loaded until motion is allowed or the visitor presses Play.
      if (!video.getAttribute('src')) video.src = VIDEO.heroMetalShaping;
      video.muted = true;
      void video.play().catch(() => {
        // A blocked autoplay attempt leaves the poster and a working Play button.
        setIsPlaying(false);
      });
    } else {
      video.pause();
    }
  }, []);
  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      if (!hasManualChoice.current) wantsPlayback.current = !preference.matches;
      syncPlayback();
    };
    const observer = new IntersectionObserver(([entry]) => {
      isInView.current = entry.isIntersecting;
      syncPlayback();
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    preference.addEventListener('change', updatePreference);
    document.addEventListener('visibilitychange', syncPlayback);
    updatePreference();
    return () => {
      observer.disconnect();
      preference.removeEventListener('change', updatePreference);
      document.removeEventListener('visibilitychange', syncPlayback);
      videoRef.current?.pause();
    };
  }, [syncPlayback]);
  const togglePlayback = () => {
    hasManualChoice.current = true;
    wantsPlayback.current = videoRef.current?.paused ?? true;
    syncPlayback();
  };
  return <>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" data-id="element-214" data-mp="v096xqsv4h6i">
        <img src={IMAGES.heroMetalPoster} width={1536} height={672} alt="" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-215" data-mp="q3ir94kyznlm" />
        <video ref={videoRef} id="hero-background-video" muted loop playsInline preload="none" poster={IMAGES.heroMetalPoster} tabIndex={-1} onPlaying={() => {
        setHasVideoFrame(true);
        setIsPlaying(true);
      }} onPause={() => setIsPlaying(false)} onError={() => {
        hasFailed.current = true;
        wantsPlayback.current = false;
        setVideoFailed(true);
        setIsPlaying(false);
      }} className={`absolute inset-0 h-full w-full object-cover object-center ${hasVideoFrame && !videoFailed ? 'opacity-100' : 'opacity-0'}`} data-id="element-216" data-mp="1ip24tst1oz3r" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,16,17,0.82)_0%,rgba(15,16,17,0.55)_55%,rgba(15,16,17,0.86)_100%)] lg:bg-[linear-gradient(90deg,rgba(15,16,17,0.85)_0%,rgba(15,16,17,0.78)_30%,rgba(15,16,17,0.25)_58%,rgba(15,16,17,0.55)_100%)]" data-id="element-217" data-mp="2e0i5w7wc0glw" />
      </div>

      <div className="relative mx-auto flex min-h-[48px] max-w-[1240px] justify-end px-5 pb-3 sm:px-8" data-id="element-218" data-mp="2nsnv59v78jxe">
        {!videoFailed && <button type="button" onClick={togglePlayback} aria-controls="hero-background-video" className="inline-flex min-h-[44px] items-center gap-2 border border-white/35 bg-charcoal/70 px-3 text-[12px] text-white transition-colors hover:bg-charcoal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" data-id="element-219" data-mp="2wqhs8n0sfhlm">
            <span aria-hidden="true" data-id="element-220" data-mp="2xs4307bmk0xp">{isPlaying ? 'Ⅱ' : '▶'}</span>
            {isPlaying ? 'Pause background video' : 'Play background video'}
          </button>}
      </div>
    </>;
}